<template>
  <v-row align="center">
    <v-col class="shrink pr-2">
      <v-icon>date_range</v-icon>
    </v-col>
    <v-col class="grow">
      <vs-text data-test="planned-time">Planned for {{ formattedPlannedTime }}</vs-text>
      <vs-text data-test="actual-time">{{ formattedActualTime }}</vs-text>
    </v-col>
    <v-col class="shrink">
      <vs-text data-test="planned-duration">{{ formattedPlannedDuration }}</vs-text>
      <vs-text data-test="actual-duration">{{ formattedActualDuration }}</vs-text>
    </v-col>
    <v-col v-if="canEditCompletedJobActualTimes" class="shrink">
      <vs-button
        icon="edit"
        small
        type="secondary"
        @click="showJobPlannedActualTimesEdit"
        data-test="edit-button"
      />
    </v-col>
  </v-row>
</template>

<script setup lang="ts">
import VsButton from '@/components/vision/VsButton.vue'
import VsText from '@/components/vision/VsText.vue'
import { formatDateTime, formatDurationAsHours, formatTimeRange } from '@/utils/date-formatting'
import Job from '@/models/job'
import { useJobBottomSheetStatesStore } from '@/components/jobs/jobBottomSheetStatesStore'
import { useStore } from 'vuex-composition-helpers'
import { computed } from 'vue'
import moment from 'moment'

const props = defineProps<{
  job: Job
  showEditButton: boolean
}>()

const store = useStore()
const jobBottomSheetStatesStore = useJobBottomSheetStatesStore()

const formattedPlannedTime = computed(() =>
  formatTimeRange(props.job.startTime, props.job.endTime, 'to'),
)
const formattedActualTime = computed(() => {
  if (
    !props.job.activeTime ||
    moment(props.job.activeTime).isSame(moment(props.job.completedTime), 'minute')
  ) {
    return 'Completed ' + formatDateTime(props.job.completedTime)
  }

  if (props.job.activeTime && !props.job.completedTime) {
    return 'Activated ' + formatDateTime(props.job.activeTime)
  }

  return 'Executed ' + formatTimeRange(props.job.activeTime, props.job.completedTime, 'to')
})

const formattedPlannedDuration = computed(() =>
  formatDurationAsHours(props.job.estimatedDuration, true),
)

const formattedActualDuration = computed(() =>
  props.job.completedTime
    ? formatDurationAsHours(moment.duration(props.job.activeDuration), true)
    : formatDurationAsHours(props.job.calculatedActiveDuration, true),
)

const canModifyCompletedJobActualTimes = computed(
  () => store.getters['permissions/canModifyCompletedJobActualTimes'],
)

const canEditCompletedJobActualTimes = computed(
  () =>
    props.showEditButton &&
    canModifyCompletedJobActualTimes.value(props.job.hubId, props.job.accountId),
)

const showJobPlannedActualTimesEdit = () => {
  jobBottomSheetStatesStore.setJobBottomSheetStates({
    isEditingPlannedActualTimesBottomSheetVisible: true,
  })
}
</script>

<style scoped></style>
